import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['menu', 'menuButton', 'menuIcon', 'dropdown', 'dropdownMenu', 'dropdownTrigger', 'dropdownItem', 'mobileDropdown', 'mobileSubmenu']

  connect() {
    // Close dropdown when clicking outside
    this.handleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener('click', this.handleClickOutside)

    // Handle keyboard navigation
    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.handleKeydown)

    // Set up ARIA controls
    this.setupAriaControls()
  }

  disconnect() {
    document.removeEventListener('click', this.handleClickOutside)
    document.removeEventListener('keydown', this.handleKeydown)
  }

  setupAriaControls() {
    if (this.hasDropdownTriggerTarget && this.hasDropdownMenuTarget) {
      const menuId = 'navbar-dropdown-menu'
      this.dropdownMenuTarget.setAttribute('id', menuId)
      this.dropdownTriggerTarget.setAttribute('aria-controls', menuId)
    }
  }

  toggle() {
    const isOpen = this.menuTarget.classList.contains('is-open')

    if (isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    this.menuTarget.classList.add('is-open')
    this.menuButtonTarget.setAttribute('aria-expanded', 'true')
    document.body.style.overflow = 'hidden'
  }

  close() {
    this.menuTarget.classList.remove('is-open')
    this.menuButtonTarget.setAttribute('aria-expanded', 'false')
    document.body.style.overflow = ''

    // Also close mobile submenu and reset its aria-expanded state
    if (this.hasMobileDropdownTarget) {
      this.mobileDropdownTarget.classList.remove('is-open')
      const trigger = this.mobileDropdownTarget.querySelector('.c-mobile-menu-group-trigger')
      trigger?.setAttribute('aria-expanded', 'false')
    }
  }

  toggleDropdown(event) {
    event.stopPropagation()

    if (!this.hasDropdownTarget) return

    const isOpen = this.dropdownTarget.classList.contains('is-open')
    const trigger = this.dropdownTarget.querySelector('.c-navbar-dropdown-trigger')

    if (isOpen) {
      this.closeDropdown()
    } else {
      this.dropdownTarget.classList.add('is-open')
      trigger?.setAttribute('aria-expanded', 'true')
    }
  }

  closeDropdown() {
    if (!this.hasDropdownTarget) return

    const trigger = this.dropdownTarget.querySelector('.c-navbar-dropdown-trigger')
    this.dropdownTarget.classList.remove('is-open')
    trigger?.setAttribute('aria-expanded', 'false')
  }

  toggleMobileDropdown(event) {
    event.preventDefault()
    event.stopPropagation()

    if (!this.hasMobileDropdownTarget) return

    const isOpen = this.mobileDropdownTarget.classList.contains('is-open')
    const trigger = this.mobileDropdownTarget.querySelector('.c-mobile-menu-group-trigger')

    if (isOpen) {
      this.mobileDropdownTarget.classList.remove('is-open')
      trigger?.setAttribute('aria-expanded', 'false')
    } else {
      this.mobileDropdownTarget.classList.add('is-open')
      trigger?.setAttribute('aria-expanded', 'true')
    }
  }

  handleClickOutside(event) {
    // Close desktop dropdown if clicking outside
    if (this.hasDropdownTarget && !this.dropdownTarget.contains(event.target)) {
      this.closeDropdown()
    }
  }

  handleKeydown(event) {
    // Handle Escape key
    if (event.key === 'Escape') {
      this.closeDropdown()

      // Close mobile menu if open
      if (this.menuTarget.classList.contains('is-open')) {
        this.close()
      }
      return
    }

    // Only handle arrow keys when dropdown is open or trigger is focused
    if (!this.hasDropdownTarget) return

    const isDropdownOpen = this.dropdownTarget.classList.contains('is-open')
    const isTriggerFocused = document.activeElement === this.dropdownTriggerTarget
    const isItemFocused = this.hasDropdownItemTarget &&
      this.dropdownItemTargets.includes(document.activeElement)

    // Open dropdown with ArrowDown when trigger is focused
    if (isTriggerFocused && event.key === 'ArrowDown') {
      event.preventDefault()
      if (!isDropdownOpen) {
        this.dropdownTarget.classList.add('is-open')
        this.dropdownTriggerTarget?.setAttribute('aria-expanded', 'true')
      }
      // Focus first item
      this.focusDropdownItem(0)
      return
    }

    // Navigate within dropdown
    if (isDropdownOpen && (isItemFocused || isTriggerFocused)) {
      const items = this.dropdownItemTargets
      const currentIndex = items.indexOf(document.activeElement)

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          this.focusDropdownItem(currentIndex + 1)
          break
        case 'ArrowUp':
          event.preventDefault()
          if (currentIndex <= 0) {
            // Return to trigger
            this.dropdownTriggerTarget?.focus()
          } else {
            this.focusDropdownItem(currentIndex - 1)
          }
          break
        case 'Home':
          event.preventDefault()
          this.focusDropdownItem(0)
          break
        case 'End':
          event.preventDefault()
          this.focusDropdownItem(items.length - 1)
          break
        case 'Tab':
          // Close dropdown when tabbing away
          this.closeDropdown()
          break
      }
    }
  }

  focusDropdownItem(index) {
    const items = this.dropdownItemTargets
    if (items.length === 0) return

    // Wrap around
    const targetIndex = Math.max(0, Math.min(index, items.length - 1))
    items[targetIndex]?.focus()
  }
}
